import { useState } from "react";
import { Link } from "react-router-dom";
import { Segmented } from "antd";
import type { ResponseGroup, SlaTarget, TicketResponseReport } from "../../../queries/reports";
import type { Severity } from "../../../types/interfaces";
import { routes } from "../../../navigation/routes/routes";
import { StatusTag } from "../../ui/StatusTag";
import { DashCard } from "./DashCard";
import { SeverityTag } from "./Tags";
import { formatDuration } from "./format";
import styles from "./dash.module.css";

type Group = "severity" | "project" | "assignee";

/** Bullet bar: average vs the SLA target, with a marker at the target. */
export const TargetBar = ({ value, target, label }: { value: number | null; target: number; label: string }) => {
  const scaleMax = Math.max(value ?? 0, target) * 1.25 || 1;
  const over = value !== null && value > target;
  return (
    <div className={styles.target}>
      <div className={styles.targetTrack} role="img"
        aria-label={`${label}: ${formatDuration(value)} average against a ${formatDuration(target)} target${over ? ", over target" : ""}`}>
        {value !== null && (
          <span
            className={styles.targetFill}
            style={{ width: `${(value / scaleMax) * 100}%`, background: over ? "var(--osi-danger)" : "var(--osi-ok)" }}
          />
        )}
        <span className={styles.targetMark} style={{ left: `${(target / scaleMax) * 100}%` }} />
      </div>
      <span className={`${styles.targetText} osi-num`}>
        {formatDuration(value)} <span className={styles.muted}>/ {formatDuration(target)} target</span>
        {over && <strong className={styles.lateTag}> Over</strong>}
      </span>
    </div>
  );
};

const Cell = ({ avg, median, target, label }: { avg: number | null; median: number | null; target?: number; label: string }) => (
  <div>
    {target !== undefined ? (
      <TargetBar value={avg} target={target} label={label} />
    ) : (
      <span className="osi-num">{formatDuration(avg)}</span>
    )}
    <div className={`${styles.muted} osi-num`}>Median {formatDuration(median)}</div>
  </div>
);

const Breach = ({ n }: { n: number }) =>
  n > 0 ? <strong className={`${styles.lateTag} osi-num`}>{n} breached</strong> : <span className={`${styles.muted} osi-num`}>0</span>;

interface Props {
  report?: TicketResponseReport;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

export const ResponseSection = ({ report, loading, error, onRetry }: Props) => {
  const [group, setGroup] = useState<Group>("severity");
  const rows: ResponseGroup[] = report ? (group === "severity" ? report.bySeverity : group === "project" ? report.byProject : report.byAssignee) : [];
  const targetFor = (r: ResponseGroup): SlaTarget | undefined =>
    group === "severity" && report ? report.targets[r.key as Severity] : undefined;
  const atRisk = report?.atRisk || [];

  return (
    <DashCard
      title="Ticket response time"
      helper="Average and median first response and resolution against each severity's SLA target."
      controls={
        <Segmented
          aria-label="Group response times by"
          value={group}
          onChange={(v) => setGroup(v as Group)}
          options={[
            { label: "Severity", value: "severity" },
            { label: "Project", value: "project" },
            { label: "Assignee", value: "assignee" },
          ]}
          className={styles.segmented}
        />
      }
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={!report || (report.summary.count === 0 && atRisk.length === 0)}
      emptyTitle="No tickets in this range"
      emptyText="Response times appear once tickets are opened in the selected period."
    >
      {group !== "severity" && (
        <p className={styles.note}>SLA targets are defined per severity, so the target markers show in the severity view.</p>
      )}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">{group === "severity" ? "Severity" : group === "project" ? "Project" : "Assignee"}</th>
              <th scope="col" className={styles.num}>Tickets</th>
              <th scope="col">First response</th>
              <th scope="col">Resolution</th>
              <th scope="col">Response breaches</th>
              <th scope="col">Resolution breaches</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className={styles.muted}>No tickets for this grouping.</td></tr>
            )}
            {rows.map((r) => {
              const t = targetFor(r);
              return (
                <tr key={r.key}>
                  <th scope="row" className={styles.rowHead}>
                    {group === "severity" ? <SeverityTag severity={r.key as Severity} /> : r.name}
                  </th>
                  <td className={`${styles.num} osi-num`}>{r.count}</td>
                  <td className={styles.wideCell}>
                    <Cell avg={r.avgFirstResponseMinutes} median={r.medianFirstResponseMinutes} target={t?.firstResponseMinutes} label={`${r.name} first response`} />
                  </td>
                  <td className={styles.wideCell}>
                    <Cell avg={r.avgResolutionMinutes} median={r.medianResolutionMinutes} target={t?.resolutionMinutes} label={`${r.name} resolution`} />
                  </td>
                  <td><Breach n={r.firstResponseBreaches} /></td>
                  <td><Breach n={r.resolutionBreaches} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3 className={styles.subTitle}>At-risk tickets</h3>
      <p className={styles.note}>Open tickets that have used 75% or more of their SLA time.</p>
      {atRisk.length === 0 ? (
        <p className={styles.muted}>No tickets at risk right now.</p>
      ) : (
        <ul className={styles.plainList}>
          {atRisk.map((t) => {
            const pct = Math.round(t.consumed * 100);
            return (
              <li key={`${t.ticketId}-${t.phase}`} className={styles.riskRow}>
                <Link className={styles.link} to={routes.ticketDetail.replace(":id", t.ticketId)}>{t.subject}</Link>
                <span className={styles.muted}>{t.projectName || "No project"}</span>
                <SeverityTag severity={t.severity} />
                <span>{t.phase === "first_response" ? "Awaiting first response" : "Awaiting resolution"}</span>
                <span className={styles.riskPct}>
                  <span className={styles.riskTrack} aria-hidden="true">
                    <span
                      className={styles.riskFill}
                      style={{ width: `${Math.min(pct, 100)}%`, background: t.breached ? "var(--osi-danger)" : "var(--osi-warn)" }}
                    />
                  </span>
                  <span className="osi-num">{pct}% of SLA used</span>
                  {t.breached && <StatusTag label="Breached" tone="danger" dot />}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </DashCard>
  );
};
